import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { listCharges } from '../api/endpoints/charges';
import { getFiscalProfile, getInvoice } from '../api/endpoints/invoices';
import type { ChargeRecord } from '../api/schemas';
import { ErrorBanner } from '../components/ErrorBanner';
import { FacturaSheet } from '../components/FacturaSheet';
import { LoadingState } from '../components/LoadingState';
import { FISCAL_PROFILE_QUERY_KEY } from './Facturas';

/**
 * How far back the print view walks the charges list looking for the line
 * items. The contract has no GET /v1/business/charges/:chargeId, so this is a
 * paged scan; at the 20-row default page size it reaches ~600 cobros back,
 * months of counter work. Beyond that the sheet says so out loud rather than
 * inventing a line.
 * ponytail: linear scan, swap for a by-id GET the day the contract grows one.
 */
const CHARGE_LOOKUP_MAX_PAGES = 30;

async function findCharge(chargeId: string): Promise<ChargeRecord | null> {
  let cursor: string | undefined;
  for (let page = 0; page < CHARGE_LOOKUP_MAX_PAGES; page += 1) {
    const result = await listCharges(cursor);
    const match = result.data.find((charge) => charge.id === chargeId);
    if (match) return match;
    if (!result.nextCursor) return null;
    cursor = result.nextCursor;
  }
  return null;
}

export function FacturaPrintPage() {
  const { invoiceId } = useParams({ from: '/facturas/$invoiceId/imprimir' });
  const invoiceQuery = useQuery({
    queryKey: ['business-invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
  });
  const profileQuery = useQuery({
    queryKey: FISCAL_PROFILE_QUERY_KEY,
    queryFn: getFiscalProfile,
    retry: false,
  });
  const chargeId = invoiceQuery.data?.chargeId;
  const chargeQuery = useQuery({
    queryKey: ['business-invoice-charge', chargeId],
    queryFn: () => findCharge(chargeId as string),
    enabled: Boolean(chargeId),
  });

  if (invoiceQuery.isLoading) {
    return (
      <main>
        <LoadingState label="Cargando factura…" />
      </main>
    );
  }

  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <main>
        <ErrorBanner message={errorMessage(invoiceQuery.error)} />
        <Link to="/facturas" className="btn small secondary">
          Volver a facturas
        </Link>
      </main>
    );
  }

  const invoice = invoiceQuery.data;
  const profile = profileQuery.data;
  const charge = chargeQuery.data ?? null;
  const detailPending = Boolean(chargeId) && chargeQuery.isPending;

  return (
    <main>
      <div className="factura-toolbar">
        <Link to="/facturas" className="btn small secondary">
          Volver
        </Link>
        <button type="button" className="btn small" onClick={() => window.print()}>
          Imprimir
        </button>
      </div>

      <FacturaSheet
        factura={{
          emisor: profile
            ? {
                razonSocial: profile.razonSocial,
                ruc: profile.ruc,
                dv: profile.dv,
                direccion: profile.direccion,
              }
            : null,
          displayNumber: invoice.displayNumber,
          status: invoice.status,
          issuedAt: invoice.issuedAt,
          clientName: invoice.clientName,
          clientRuc: invoice.clientRuc,
          clientDv: invoice.clientDv,
          subtotalCents: invoice.subtotalCents,
          discountCents: invoice.discountCents,
          itbmsRateBp: invoice.itbmsRateBp,
          itbmsCents: invoice.itbmsCents,
          totalCents: invoice.totalCents,
          voidReason: invoice.voidReason,
          lines:
            charge?.items.map((item) => ({
              name: item.name,
              quantity: item.quantity,
              lineTotalCents: item.lineTotalCents,
            })) ?? null,
          linesPending: detailPending,
        }}
      />
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar la factura.';
}
